import type { ColDef, GridOptions, ValueGetterParams } from 'ag-grid-community';

import { TableHeaderWithTooltipIcon, TextInputCell, type TypeTextInputCellProps } from '~/components/common';
import {
  ReceiveRequestDateCell,
  ReceiveRequestQuantityInputCell,
  ReceiveRequestSelectCell,
} from '~/components/receive';

import { formatNumber } from '~/utils/common';

import { LOT_NO_MAX_LENGTH, LOT_NO_REGEX } from '~/constants';

import type { TypeReceiveRequestRegisterModalGoods } from '~/types/receive.types';

/**
 * [입고관리 > 입고요청관리] 입고요청등록 모달 > 1페이지 > 입고 상품 테이블
 */
const getReceiveRegisterModalColumnDefs = ({
  hasPermissionNFA,
  data,
  setData,
}: {
  hasPermissionNFA: boolean;
  data: TypeReceiveRequestRegisterModalGoods[];
  setData: (goodsList: TypeReceiveRequestRegisterModalGoods[]) => void;
}) => {
  const gridOptions: GridOptions = {};

  const columnDefs: ColDef<TypeReceiveRequestRegisterModalGoods>[] = [
    {
      headerName: '',
      valueFormatter: () => '',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 44,
    },
    {
      field: 'goodsCode',
      headerName: '상품코드',
      width: 140,
    },
    {
      hide: !hasPermissionNFA,
      field: 'nsid',
      headerName: 'NSID',
      width: 180,
    },
    {
      field: 'alternativeCode',
      headerName: '상품바코드',
      width: 150,
    },
    {
      field: 'goodsName',
      headerName: '상품명',
      minWidth: 240,
      flex: 1,
    },
    {
      field: 'kurlyKeepTypeDesc',
      headerName: '보관유형',
      width: 100,
    },
    {
      headerName: '기한/일자 사용여부',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '유통/소비기한 또는 제조일자가 없다면\n[사용] 체크박스를 해제해 주세요.',
        tooltipMaxWidth: '203px',
      },
      cellRenderer: ReceiveRequestDateCell,
      cellClass: 'overflow-visible',
      width: 309,
      minWidth: 309,
    },
    {
      field: 'palletYn',
      headerName: '파렛트 사용여부',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '상품을 파렛트에 적재 후 입고하면\n[사용함]으로 선택해 주세요.',
        tooltipMaxWidth: '182px',
      },
      cellRenderer: ReceiveRequestSelectCell,
      cellClass: 'overflow-visible',
      width: 136,
      minWidth: 136,
    },
    {
      field: 'lotNo',
      headerName: 'LOT 번호',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription:
          '상품의 제조 또는 생산 단위를 구분하기 위한 고유 식별 번호입니다.\n입력된 LOT 번호는 재고 추적 및 이력 관리에 활용됩니다.',
        tooltipMaxWidth: '335px',
      },
      cellRenderer: TextInputCell,
      cellRendererParams: (
        prop: TypeTextInputCellProps<
          TypeReceiveRequestRegisterModalGoods,
          TypeReceiveRequestRegisterModalGoods['lotNo']
        >
      ) => ({
        placeholder: `${LOT_NO_MAX_LENGTH}자 이하 입력`,
        maxLength: LOT_NO_MAX_LENGTH,
        onChange: (value: string) => {
          if (!LOT_NO_REGEX.test(value)) return;

          const rowIndex = prop.node.rowIndex;

          if (rowIndex !== null) {
            setData([
              ...data.slice(0, rowIndex),
              {
                ...data[rowIndex],
                lotNo: value,
              },
              ...data.slice(rowIndex + 1),
            ]);
          }
        },
        onBlur: (value: string) => {
          const rowIndex = prop.node.rowIndex;

          if (rowIndex !== null) {
            setData([
              ...data.slice(0, rowIndex),
              {
                ...data[rowIndex],
                lotNo: value.trim(),
              },
              ...data.slice(rowIndex + 1),
            ]);
          }
        },
      }),
      width: 200,
      minWidth: 200,
    },
    {
      field: 'boxQty',
      headerName: '수량(BOX)',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.',
        tooltipMaxWidth: '171px',
      },
      cellRenderer: ReceiveRequestQuantityInputCell,
      width: 120,
      minWidth: 120,
    },
    {
      field: 'boxPerQty',
      headerName: '입수(BOX당)',
      cellRenderer: ReceiveRequestQuantityInputCell,
      width: 120,
      minWidth: 120,
    },
    {
      field: 'eachQty',
      headerName: '수량(EA)',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX) 또는 수량(EA)중\n하나는 필수로 입력해야 합니다.',
        tooltipMaxWidth: '171px',
      },
      cellRenderer: ReceiveRequestQuantityInputCell,
      width: 120,
      minWidth: 120,
    },
    {
      headerName: '총 수량',
      headerComponent: TableHeaderWithTooltipIcon,
      headerComponentParams: {
        tooltipDescription: '수량(BOX)*입수(BOX당)+수량(EA)로\n자동 계산됩니다.',
        tooltipMaxWidth: '210px',
      },
      cellStyle: { textAlign: 'right' },
      valueGetter: ({ data }: ValueGetterParams<TypeReceiveRequestRegisterModalGoods>) => {
        if (!data) return '-';
        return data.totalQty ? formatNumber(data.totalQty) : '-';
      },
      width: 112,
    },
  ];

  return { gridOptions, columnDefs };
};

export default getReceiveRegisterModalColumnDefs;
